import { useEffect, useState } from "react";
import { api, formatErr } from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../components/ui/select";
import { Check } from "lucide-react";
import { toast } from "sonner";
import { Field, SectionCard, DetailField } from "./shared";
import { Link } from "react-router-dom";
import { ACCOUNTING_BASE } from "./accountingNav";

/**
 * Accounting Phase 7 - "Migration / Go-Live" settings page
 * (/client/society/accounting/migration).
 *
 * Deliberately a NEW, engine-aware implementation replacing the earlier
 * Phase 2-era placeholder that used to live at this route (which wrote to
 * the legacy, single-entry society_accounting_migrations/society_accounting_setup
 * collections - those backend endpoints are left completely untouched and
 * unreachable from the UI as of this phase, not deleted). This page now
 * only stores the Go-Live PLAN (Financial Year, Go-Live Date, Migration
 * Type, Previous Software, Notes) plus a lightweight "Confirmed" governance
 * stamp. The actual opening balances themselves - and the one real,
 * balanced OPENING voucher posted through the Phase 3 engine - live on the
 * separate Opening Balances page (/client/society/accounting/opening-balances).
 *
 * Item 3 (Go-Live boundary): the moment Go-Live Date is saved here (even
 * before this page's own "Confirm Go-Live Plan" step), Phase 6's Billing
 * integration stops auto-posting historical bills/payments dated earlier
 * than it - see _post_bill_to_accounting / _post_bill_payment_to_accounting
 * in server.py.
 */
export default function AccountingMigrationPage() {
  const [settings, setSettings] = useState(null);
  const [financialYears, setFinancialYears] = useState([]);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [settingsRes, fyRes] = await Promise.all([
        api.get("/society/accounting/golive-settings"),
        api.get("/society/accounting/engine/financial-years").catch(() => ({ data: [] })),
      ]);
      setSettings(settingsRes.data || {});
      setFinancialYears(Array.isArray(fyRes.data) ? fyRes.data : []);
      setForm({
        financialYearId: settingsRes.data?.financialYearId || "",
        goLiveDate: settingsRes.data?.goLiveDate || "",
        migrationType: settingsRes.data?.migrationType || "opening_balance",
        previousSoftware: settingsRes.data?.previousSoftware || "",
        notes: settingsRes.data?.notes || "",
      });
    } catch (err) {
      toast.error(formatErr(err, "Could not load Go-Live settings"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      setBusy(true);
      await api.put("/society/accounting/golive-settings", form);
      toast.success("Go-Live settings saved");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not save Go-Live settings"));
    } finally {
      setBusy(false);
    }
  };

  const confirmPlan = async () => {
    if (!window.confirm("Confirm this Go-Live plan? This locks in the Financial Year and Go-Live Date as reviewed and agreed.")) return;
    try {
      setBusy(true);
      await api.post("/society/accounting/golive-settings/confirm");
      toast.success("Go-Live plan confirmed");
      await load();
    } catch (err) {
      toast.error(formatErr(err, "Could not confirm Go-Live plan"));
    } finally {
      setBusy(false);
    }
  };

  if (loading || !form) return <div className="text-sm text-[#6B7280]">Loading...</div>;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Migration / Go-Live</h2>
        <p className="mt-1 text-sm text-[#6B7280]">
          Set the Financial Year and Go-Live Date this Society's accounting goes live from. From this date onward, Billing
          automatically posts to Accounting (Phase 6). Everything before it is brought in as Opening Balances, never as
          fabricated historical vouchers - see the Opening Balances page.
        </p>
      </div>

      <SectionCard
        title="Go-Live plan"
        subtitle={settings?.status === "confirmed" ? `Confirmed by ${settings.confirmedBy || "-"} on ${settings.confirmedAt ? new Date(settings.confirmedAt).toLocaleString() : "-"}` : "Not yet confirmed"}
        actions={<Button disabled={busy} onClick={confirmPlan} className="bg-emerald-600 text-white hover:bg-emerald-700"><Check className="mr-2 h-4 w-4" /> Confirm Go-Live Plan</Button>}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Financial Year">
            <Select value={form.financialYearId} onValueChange={(v) => setForm((f) => ({ ...f, financialYearId: v }))}>
              <SelectTrigger><SelectValue placeholder="Select Financial Year" /></SelectTrigger>
              <SelectContent>
                {financialYears.map((fy) => <SelectItem key={fy.id} value={fy.id}>{fy.code} ({fy.startDate} to {fy.endDate})</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Go-Live Date">
            <Input type="date" value={form.goLiveDate} onChange={(e) => setForm((f) => ({ ...f, goLiveDate: e.target.value }))} />
          </Field>
          <Field label="Migration Type">
            <Select value={form.migrationType} onValueChange={(v) => setForm((f) => ({ ...f, migrationType: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="opening_balance">Opening Balances Only</SelectItem>
                <SelectItem value="full_historical">Full Historical Records</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Previous Software / Source">
            <Input value={form.previousSoftware} onChange={(e) => setForm((f) => ({ ...f, previousSoftware: e.target.value }))} placeholder="e.g. Excel register, MyGate, ADDA" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Migration Notes">
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Previous accountant/CA reference, AGM note, anything relevant for audit" />
            </Field>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button disabled={busy} onClick={save} className="bg-[#111827] text-white hover:bg-[#374151]">Save Go-Live Settings</Button>
          <Link to={`${ACCOUNTING_BASE}/opening-balances`} className="text-sm font-semibold text-[#111827] underline">Go to Opening Balances &rarr;</Link>
        </div>
      </SectionCard>

      {settings?.goLiveDate && (
        <SectionCard title="Effective boundary">
          <div className="grid gap-4 sm:grid-cols-2">
            <DetailField label="Go-Live Date" value={settings.goLiveDate} />
            <DetailField label="Status" value={settings.status === "confirmed" ? "Confirmed" : "Draft"} />
          </div>
          <p className="mt-3 text-xs text-[#6B7280]">
            Bills/payments dated before {settings.goLiveDate} are no longer auto-posted by Billing integration - they are expected to be represented in Opening Balances instead.
          </p>
        </SectionCard>
      )}
    </div>
  );
}
